import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  IconButton,
  Typography,
  Modal,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/system';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { negotiation as negotiationDoc } from '../../api/firebase/firestore/negotiation';
import { orderSchema } from '../../api/firebase/firestore/negotiation/validation';
import {
  black,
  blueButton,
  gray,
  primaryButtonBackgroundColor,
  primarycolor,
  tertiaryButtonBackground
} from '../../constants/Colors';

import CustomDatePicker from './CustomDatePicker';
import CustomRadioButton from './CustomRadioButton';
import CustomTextfield from './CustomTextfield';

import type { OrderType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { Dayjs } from 'dayjs';

const theme = createTheme({
  palette: {
    text: {
      disabled: black
    }
  }
});

const styles = {
  blueButton,
  label: {
    fontSize: '12px'
  },
  modal: {
    '@media screen and (max-width:480px)': {
      margin: '0 16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    p: 2
  },
  paper: {
    '@media screen and (max-width:480px)': {
      padding: '24px 16px',
      width: '100%'
    },
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    padding: '24px 0',
    position: 'relative',
    width: '480px'
  },
  primaryButtonBackgroundColor,
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  tertiaryButtonBackground
};

interface Props {
  open: boolean;
  handleClose: () => void;
}

interface CheckboxGroupProps {
  defaultChecked: number[];
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ defaultChecked }) => {
  const { t } = useTranslation();
  const initialState = {
    fifth: defaultChecked.includes(5),
    first: defaultChecked.includes(1),
    fourth: defaultChecked.includes(4),
    second: defaultChecked.includes(2),
    third: defaultChecked.includes(3)
  };

  const [state] = React.useState(initialState);

  const CustomCheckbox = styled(Checkbox)({
    '&.Mui-checked': {
      color: black
    },

    '&.Mui-disabled': {
      color: black
    },

    '&.MuiCheckbox-root': {
      color: black
    }
  });

  return (
    <div>
      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.first} name="first" />}
        label={t('message.order.compensation.1')}
      />

      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.second} name="second" />}
        label={t('message.order.compensation.2')}
      />

      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.third} name="third" />}
        label={t('message.order.compensation.3')}
      />

      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.fourth} name="fourth" />}
        label={t('message.order.compensation.4')}
      />

      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.fifth} name="fifth" />}
        label={t('message.order.compensation.5')}
      />

      <FormControlLabel
        disabled
        control={<CustomCheckbox checked={state.fifth} name="fifth" />}
        label={t('message.order.compensation.6')}
      />
    </div>
  );
};

const ConfirmOrderModal = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const { open, handleClose } = props;

  const {
    control,
    setValue,

    reset,
    watch,
    formState: { errors }
  } = useForm<OrderType>({
    defaultValues: {
      publishEndDate: null,
      publishStartDate: null,
      reportSubmissionDate: null
    },
    resolver: yupResolver(orderSchema)
  });

  const { id } = useParams<{ id: string }>();

  const checkboxBooleanArray = (data: number[], length?: number): boolean[] => {
    if (length) {
      const newArray = [];
      for (let i = 0; i <= length; i++) {
        newArray.push(false);
      }
      data.forEach((element: number) => {
        newArray[element] = true;
      });
      return newArray;
    }
    return [];
  };

  const [, setOrderId] = useState('');

  const [compensations, setCompensations] = useState<number[]>([]);

  const initOrder = (): void => {
    const negotiationDocument = negotiationDoc(id ?? '');
    const ordersQuery = query(collection(negotiationDocument, 'orders'));
    onSnapshot(ordersQuery, (snapshot) => {
      if (snapshot.docs.length > 0) {
        const document = snapshot.docs[0];
        const data = document.data();

        setOrderId(document.id);

        setCompensations(data.compensation as number[]);

        reset({
          amount: data.amount as number,
          chatId: data.chatId as string,
          companyName: data.companyName as string,
          compensation: checkboxBooleanArray(
            data.compensation as number[],
            (data.compensation as number[]).length
          ),
          created: (data.created as Dayjs).toDate(),
          depositTiming: data.depositTiming as string,
          kpi: data.kpi as string,
          optionContent: data.optionContent as string,
          optionFlg: data.optionFlg === 1 ? 'true' : 'false',
          orderId: document.id,
          pkgName: data.pkgName as string,
          publishEndDate: (data.publishEndDate as Dayjs)?.toDate() ?? null,
          publishStartDate: (data.publishStartDate as Dayjs)?.toDate() ?? null,
          reportFlg: data.reportFlg === 1 ? 'true' : 'false',
          reportSubmissionDate:
            (data.reportSubmissionDate as Dayjs)?.toDate() ?? null,
          senderId: data.senderId as string,
          transferTiming: data.transferTiming === 1 ? 'true' : 'false'
        });
      }
    });
  };

  useEffect(() => {
    initOrder();
  }, [id, open]);

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={styles.modal}>
        <Typography sx={styles.paper}>
          <Typography
            sx={{
              '@media screen and (max-width:480px)': {
                p: 0
              },
              p: '0 40px'
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={handleClose}
              style={{
                color: '#757575',
                position: 'absolute',
                right: '40px',
                top: '24px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ color: black, fontSize: '22px', mb: '24px' }}>
              {t('message.insertOrderTitle')}
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.companyName)}
                  helperText={errors.companyName?.message}
                  labelText={t('message.order.companyName')}
                  requiredText={t('form.required')}
                  isRequired
                  disabled
                  disabledColor={black}
                  width={'article'}
                />
              )}
              control={control}
              name="companyName"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.pkgName)}
                  helperText={errors.pkgName?.message}
                  labelText={t('message.order.pkgName')}
                  requiredText={t('form.required')}
                  isRequired
                  disabled
                  disabledColor={black}
                  width={'article'}
                />
              )}
              control={control}
              name="pkgName"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.amount)}
                  helperText={errors.amount?.message}
                  labelText={t('message.order.amount')}
                  requiredText={t('form.required')}
                  isRequired
                  disabled
                  disabledColor={black}
                  width={'article'}
                />
              )}
              control={control}
              name="amount"
            />
            <Typography sx={{ alignItems: 'center', display: 'flex' }}>
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired={false}
                    error={Boolean(errors.publishStartDate)}
                    helperText={errors.publishStartDate?.message}
                    labelText={t('message.order.publishDate')}
                    disabled
                    disabledColor={black}
                    width={'100%'}
                    setValue={(newValue) => {
                      setValue('publishStartDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="publishStartDate"
              />
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  mb: '24px',
                  mt: '22px',
                  mx: 1
                }}
              >
                <Typography variant="body1" component="div">
                  〜
                </Typography>
              </Box>
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired={false}
                    error={Boolean(errors.publishEndDate)}
                    helperText={errors.publishEndDate?.message}
                    labelText={'\u3000'}
                    width={'100%'}
                    disabled
                    disabledColor={black}
                    setValue={(newValue) => {
                      setValue('publishEndDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="publishEndDate"
              />
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.kpi)}
                  helperText={errors.kpi?.message}
                  labelText={t('message.order.kpi')}
                  disabled
                  disabledColor={black}
                  requiredText={t('form.required')}
                  isRequired
                  width={'article'}
                />
              )}
              control={control}
              name="kpi"
            />
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.optionFlg)}
                  helperText={errors.optionFlg?.message}
                  labelText={t('message.order.optionFlg')}
                  disabled
                  disabledColor={black}
                  requiredText={t('form.required')}
                  isRequired
                  trueOption={t('message.order.ari')}
                  falseOption={t('message.order.nashi')}
                />
              )}
              control={control}
              name="optionFlg"
            />
            {watch('optionFlg') === 'true' && (
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.optionContent)}
                    helperText={errors.optionContent?.message}
                    labelText={t('message.order.optionContent')}
                    disabled
                    disabledColor={black}
                    requiredText={t('form.required')}
                    isRequired
                    width={'article'}
                  />
                )}
                control={control}
                name="optionContent"
              />
            )}
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.reportFlg)}
                  helperText={errors.reportFlg?.message}
                  labelText={t('message.order.reportFlg')}
                  disabled
                  disabledColor={black}
                  requiredText={t('form.required')}
                  isRequired
                  trueOption={t('message.order.ari')}
                  falseOption={t('message.order.nashi')}
                />
              )}
              control={control}
              name="reportFlg"
            />
            {watch('reportFlg') === 'true' && (
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired
                    disabled
                    disabledColor={black}
                    requiredText={t('form.required')}
                    error={Boolean(errors.reportSubmissionDate)}
                    helperText={errors.reportSubmissionDate?.message}
                    labelText={t('message.order.reportSubmissionDate')}
                    width={'50%'}
                    setValue={(newValue) => {
                      setValue('reportSubmissionDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="reportSubmissionDate"
              />
            )}
            <Typography color={gray} sx={styles.label} component="span">
              {t('message.order.compensation.label')}
            </Typography>
            <Typography component="span" sx={styles.required}>
              {t('form.required')}
            </Typography>
            <FormHelperText>
              {t('message.order.compensation.description')}
            </FormHelperText>
            <ThemeProvider theme={theme}>
              <CheckboxGroup defaultChecked={compensations} />
            </ThemeProvider>
            <Typography
              sx={{ color: black, fontSize: '14px', mb: '8px', mt: '24px' }}
            >
              {t('message.order.howToPay')}
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.transferTiming)}
                  helperText={errors.transferTiming?.message}
                  disabled
                  disabledColor={black}
                  isRequired
                  requiredText={t('form.required')}
                  labelText={t('message.order.transferTiming')}
                  trueOption={t('message.order.before')}
                  falseOption={t('message.order.after')}
                />
              )}
              control={control}
              name="transferTiming"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  disabled
                  disabledColor={black}
                  error={Boolean(errors.depositTiming)}
                  helperText={errors.depositTiming?.message}
                  labelText={t('message.order.depositTiming')}
                  isRequired
                  requiredText={t('form.required')}
                  width={'article'}
                />
              )}
              control={control}
              name="depositTiming"
            />
          </Typography>
        </Typography>
      </Modal>
    </>
  );
};

export default ConfirmOrderModal;
